import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { safetyApi, type SafetyRole, type SetupInput } from '../api/safetyApi';
import { safetyKeys } from '../queryKeys';

export function useSafetyLocationId(): string {
  const { currentLocation } = useLocation();
  return currentLocation?.id ?? '';
}

export function useSafetyContent() {
  return useQuery({
    queryKey: safetyKeys.content(),
    queryFn: () => safetyApi.getContent(),
    staleTime: 60 * 60 * 1000, // canonical content — changes only on deploy
  });
}

export function useSafetyOverview() {
  const locationId = useSafetyLocationId();
  const query = useQuery({
    queryKey: safetyKeys.overview(locationId),
    queryFn: () => safetyApi.getOverview(locationId),
    enabled: !!locationId,
  });
  return { ...query, locationId };
}

export function useSafetyRoster() {
  const locationId = useSafetyLocationId();
  return useQuery({
    queryKey: safetyKeys.roster(locationId),
    queryFn: () => safetyApi.getRoster(locationId),
    enabled: !!locationId,
  });
}

export function useSafetyStaff() {
  const locationId = useSafetyLocationId();
  const query = useQuery({
    queryKey: safetyKeys.staff(locationId),
    queryFn: () => safetyApi.listStaff(locationId),
    enabled: !!locationId,
  });
  return { ...query, locationId };
}

export function useSafetyEquipment() {
  const locationId = useSafetyLocationId();
  const query = useQuery({
    queryKey: safetyKeys.equipment(locationId),
    queryFn: () => safetyApi.listEquipment(locationId),
    enabled: !!locationId,
  });
  return { ...query, locationId };
}

export function useSafetyRecords() {
  const locationId = useSafetyLocationId();
  const query = useQuery({
    queryKey: safetyKeys.records(locationId),
    queryFn: () => safetyApi.getRecords(locationId),
    enabled: !!locationId,
  });
  return { ...query, locationId };
}

export function useSafetyCourse(assignmentId: string) {
  const locationId = useSafetyLocationId();
  const query = useQuery({
    queryKey: safetyKeys.course(locationId, assignmentId),
    queryFn: () => safetyApi.getCourse(assignmentId, locationId),
    enabled: !!locationId && !!assignmentId,
  });
  return { ...query, locationId };
}

/** Invalidate everything that depends on training/equipment state. */
function useInvalidateSafety() {
  const queryClient = useQueryClient();
  const locationId = useSafetyLocationId();
  return () => {
    queryClient.invalidateQueries({ queryKey: safetyKeys.overview(locationId) });
    queryClient.invalidateQueries({ queryKey: safetyKeys.staff(locationId) });
    queryClient.invalidateQueries({ queryKey: safetyKeys.equipment(locationId) });
    queryClient.invalidateQueries({ queryKey: safetyKeys.records(locationId) });
  };
}

export function useSetupMutation() {
  const invalidate = useInvalidateSafety();
  return useMutation({
    mutationFn: (input: SetupInput) => safetyApi.setup(input),
    onSuccess: invalidate,
  });
}

export function useSkipSetupMutation() {
  const invalidate = useInvalidateSafety();
  const locationId = useSafetyLocationId();
  return useMutation({
    mutationFn: () => safetyApi.skipSetup(locationId),
    onSuccess: invalidate,
  });
}

export function useAddStaffMutation() {
  const invalidate = useInvalidateSafety();
  const locationId = useSafetyLocationId();
  return useMutation({
    mutationFn: (input: { name: string; roles: SafetyRole[]; bbpExposed?: boolean }) =>
      safetyApi.addStaff({ locationId, ...input }),
    onSuccess: invalidate,
  });
}

export function useUpdateStaffMutation() {
  const invalidate = useInvalidateSafety();
  const locationId = useSafetyLocationId();
  return useMutation({
    mutationFn: (input: {
      id: string;
      patch: { roles?: SafetyRole[]; bbpExposed?: boolean; active?: boolean };
    }) => safetyApi.updateStaff(input.id, locationId, input.patch),
    onSuccess: invalidate,
  });
}

export function useCompleteMutation(assignmentId: string) {
  const invalidate = useInvalidateSafety();
  const locationId = useSafetyLocationId();
  return useMutation({
    mutationFn: (input: {
      answers: { itemId: string; answerIndex: number }[];
      signerName: string;
    }) => safetyApi.complete(assignmentId, locationId, input),
    onSuccess: invalidate,
  });
}

export function useLogEntryMutation() {
  const invalidate = useInvalidateSafety();
  const locationId = useSafetyLocationId();
  return useMutation({
    mutationFn: (input: { equipmentId: string; values: Record<string, string | boolean> }) =>
      safetyApi.createLogEntry(input.equipmentId, locationId, input.values),
    onSuccess: invalidate,
  });
}

export function useAddEquipmentMutation() {
  const invalidate = useInvalidateSafety();
  const locationId = useSafetyLocationId();
  return useMutation({
    mutationFn: (input: { packId: string; qty: number }) =>
      safetyApi.addEquipment({ locationId, ...input }),
    onSuccess: invalidate,
  });
}

export function useRemoveEquipmentMutation() {
  const invalidate = useInvalidateSafety();
  const locationId = useSafetyLocationId();
  return useMutation({
    mutationFn: (id: string) => safetyApi.removeEquipment(id, locationId),
    onSuccess: invalidate,
  });
}

export function useReviewEcpMutation() {
  const invalidate = useInvalidateSafety();
  const locationId = useSafetyLocationId();
  return useMutation({
    mutationFn: () => safetyApi.reviewEcp(locationId),
    onSuccess: invalidate,
  });
}
